import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null); // null = checking, false = unauthenticated, object = user
    const [error, setError] = useState("");

    const refresh = useCallback(async () => {
        try {
            const { data } = await api.get("/auth/me");
            setUser(data.user);
        } catch {
            setUser(false);
        }
    }, []);

    useEffect(() => {
        refresh();
    }, [refresh]);

    const login = async (email, password) => {
        setError("");
        try {
            const { data } = await api.post("/auth/login", { email, password });
            if (data.access_token) localStorage.setItem("access_token", data.access_token);
            sessionStorage.setItem("allied_pod_login_ts", Date.now().toString());
            setUser(data.user);
            return data.user;
        } catch (e) {
            const msg = formatApiError(e.response?.data?.detail) || e.message;
            setError(msg);
            throw new Error(msg);
        }
    };

    const register = async (name, email, password, terms_accepted = false) => {
        setError("");
        try {
            const { data } = await api.post("/auth/register", { name, email, password, terms_accepted });
            if (data.access_token) localStorage.setItem("access_token", data.access_token);
            sessionStorage.setItem("allied_pod_login_ts", Date.now().toString());
            setUser(data.user);
            return data.user;
        } catch (e) {
            const msg = formatApiError(e.response?.data?.detail) || e.message;
            setError(msg);
            throw new Error(msg);
        }
    };

    const logout = async () => {
        try {
            await api.post("/auth/logout");
        } catch (_) {}
        localStorage.removeItem("access_token");
        sessionStorage.removeItem("allied_pod_login_ts");
        setUser(false);
    };

    const value = useMemo(
        () => ({ user, error, login, register, logout, refresh }),
        [user, error, refresh],
    );

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
}

export const useAuth = () => useContext(AuthContext);
